import { useEffect, useState } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Typography,
    styled,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import WarningIcon from '@mui/icons-material/Warning';

import { ActionButton, CancelButton, SubmitButton } from 'components/ui/buttons';
import { useNotification } from 'context/notification';
import {
    getAttendeeProducts,
    updateAttendeeProducts,
    updateRequiredAttendeeInfo,
} from './RegistrationAPI';

const ATTENDEE_FIELDS = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
];

export default function RegistrationAttendeeSettings(props) {
    const { eventId, loading, onUpdate, firstName, lastName, email } = props;

    const [productState, setProductState] = useState({
        status: 'loading',
        products: [],
    });
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [infoModalOpen, setInfoModalOpen] = useState(false);

    useEffect(() => {
        let isActive = true;

        setProductState({ status: 'loading', products: [] });

        getAttendeeProducts(eventId)
            .then((data) => {
                if (!isActive) return;

                if (!Array.isArray(data.products)) {
                    throw new Error();
                }

                const products = data.products.map((p) => ({
                    id: p.product_id,
                    name: p.prod_name,
                    checked: p.is_attendee === 1,
                    type: p.prod_type_id,
                }));

                setProductState({
                    status: 'success',
                    products,
                });
            })
            .catch(() => {
                if (!isActive) return;

                setProductState({
                    status: 'error',
                    products: [],
                });
            });

        return () => {
            isActive = false;
        };
    }, [eventId]);

    return (
        <Stack spacing={3}>
            <SummaryCard>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Box>
                        <Typography sx={{ fontWeight: 'bold' }}>
                            Attendee Ticket Types
                        </Typography>
                        <Typography variant='caption'>
                            {productState.status === 'success' &&
                                `${productState.products.filter((p) => p.checked).length} of ${productState.products.length
                                } ticket types selected`}
                            {productState.status === 'loading' && 'Loading ticket types...'}
                            {productState.status === 'error' &&
                                'Unable to load attendee ticket types'}
                        </Typography>
                    </Box>
                </Box>

                <Box mt={2}>
                    {productState.status === 'loading' && (
                        <LoadingRow>
                            <CircularProgress color="secondary" size={24} />
                        </LoadingRow>
                    )}

                    {productState.status === 'error' && (
                        <WarningRow>
                            <WarningIcon fontSize="small" />
                            <Box ml={1} />
                            <WarningText>Unable to load attendee ticket types.</WarningText>
                        </WarningRow>
                    )}

                    {productState.status === 'success' && productState.products.length === 0 && (
                        <WarningRow>
                            <WarningIcon fontSize="small" />
                            <Box ml={1} />
                            <WarningText>
                                You must create at least one ticket before you can ask attendee
                                questions.
                            </WarningText>
                        </WarningRow>
                    )}

                    {productState.status === 'success' &&
                        productState.products.length > 0 &&
                        productState.products.filter((p) => p.checked).length === 0 && (
                            <WarningRow>
                                <WarningIcon fontSize="small" />
                                <Box ml={1} />
                                <WarningText>
                                    You must select at least one ticket type to ask
                                    attendee-specific questions.
                                </WarningText>
                            </WarningRow>
                        )}

                    {productState.status === 'success' &&
                        productState.products.filter((p) => p.checked).length > 0 && (
                            <Stack spacing={1}>
                                {productState.products
                                    .filter((p) => p.checked)
                                    .map((product) => (
                                        <SummaryItem key={product.id}>
                                            {product.type === 1 ? <AdmissionIcon /> : <AddonIcon />}
                                            <Typography>{product.name}</Typography>
                                        </SummaryItem>
                                    ))}
                            </Stack>
                        )}
                </Box>

                <Box mt={2} display="flex" justifyContent="flex-end">
                    <ActionButton onClick={() => setProductModalOpen(true)}>Edit</ActionButton>
                </Box>
            </SummaryCard>

            <Divider />

            <SummaryCard>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Box>
                        <Typography sx={{ fontWeight: 'bold' }}>
                            Required Attendee Information
                        </Typography>
                        <Typography variant='caption'>
                            {countRequiredFields(firstName, lastName, email)} required fields
                        </Typography>
                    </Box>
                </Box>

                <Box mt={2}>
                    {loading === true ? (
                        <LoadingRow>
                            <CircularProgress color="secondary" size={24} />
                        </LoadingRow>
                    ) : (
                        <Stack spacing={1}>
                            {ATTENDEE_FIELDS.map((field) => {
                                const active = Boolean(props[field.key]);

                                return (
                                    <SummaryItem key={field.key}>
                                        {active ? (
                                            <CheckMark>
                                                <CheckIcon fontSize="inherit" />
                                            </CheckMark>
                                        ) : (
                                            <CrossMark>
                                                <CloseIcon fontSize="inherit" />
                                            </CrossMark>
                                        )}
                                        <Typography>{field.label}</Typography>
                                    </SummaryItem>
                                );
                            })}
                        </Stack>
                    )}
                </Box>

                <Box mt={2} display="flex" justifyContent="flex-end">
                    <ActionButton
                        onClick={() => setInfoModalOpen(true)}
                        disabled={loading === true}
                    >
                        Edit
                    </ActionButton>
                </Box>
            </SummaryCard>

            <AttendeeProductsDialog
                open={productModalOpen}
                onClose={() => setProductModalOpen(false)}
                eventId={eventId}
                productState={productState}
                onSaved={(products) => {
                    setProductState({ status: 'success', products });
                    setProductModalOpen(false);
                }}
            />

            <RequiredInfoDialog
                open={infoModalOpen}
                onClose={() => setInfoModalOpen(false)}
                eventId={eventId}
                loading={loading}
                firstName={firstName}
                lastName={lastName}
                email={email}
                onUpdate={onUpdate}
                onSaved={() => setInfoModalOpen(false)}
            />
        </Stack>
    );
}

function AttendeeProductsDialog({ open, onClose, eventId, productState, onSaved }) {
    const { notify } = useNotification();
    const [state, setState] = useState({
        products: [],
        saving: false,
        loaded: false,
    });

    useEffect(() => {
        if (open !== true || productState.status !== 'success') return;

        setState({
            products: productState.products.map((product) => ({ ...product })),
            saving: false,
            loaded: true,
        });
    }, [open, productState]);

    const handleChange = (event) => {
        const id = Number(event.target.id);
        const checked = event.target.checked;

        setState((current) => ({
            ...current,
            products: current.products.map((product) =>
                product.id === id ? { ...product, checked } : product,
            ),
        }));
    };

    const handleSave = () => {
        if (state.saving) return;

        setState((current) => ({ ...current, saving: true }));

        updateAttendeeProducts(
            eventId,
            state.products.filter((product) => product.checked).map((product) => product.id),
        )
            .then(() => {
                notify.success('Attendees updated');
                onSaved(state.products);
            })
            .catch(() => {
                notify.error('Unable to save attendees');
                setState((current) => ({ ...current, saving: false }));
            });
    };

    const unsavedChanges =
        state.loaded &&
        state.products.some((product, index) => {
            const original = productState.products[index];

            return !original || original.id !== product.id || original.checked !== product.checked;
        });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit Attendee Ticket Types</DialogTitle>
            <DialogContent dividers>
                {productState.status === 'loading' && (
                    <LoadingRow>
                        <CircularProgress color="secondary" />
                    </LoadingRow>
                )}

                {productState.status === 'error' && (
                    <WarningRow>
                        <WarningIcon fontSize="small" />
                        <Box ml={1} />
                        <WarningText>Unable to load attendee ticket types.</WarningText>
                    </WarningRow>
                )}

                {productState.status === 'success' && productState.products.length === 0 && (
                    <WarningRow>
                        <WarningIcon fontSize="small" />
                        <Box ml={1} />
                        <WarningText>
                            You must create at least one ticket before you can ask attendee
                            questions.
                        </WarningText>
                    </WarningRow>
                )}

                {productState.status === 'success' &&
                    productState.products.length > 0 &&
                    state.loaded && (
                        <Stack spacing={1}>
                            {state.products.map((product) => (
                                <FormControlLabel
                                    key={product.id}
                                    label={
                                        <Box display="flex" alignItems="center">
                                            {product.type === 1 ? <AdmissionIcon /> : <AddonIcon />}
                                            <Typography>{product.name}</Typography>
                                        </Box>
                                    }
                                    control={
                                        <Checkbox
                                            id={String(product.id)}
                                            checked={product.checked}
                                            onChange={handleChange}
                                        />
                                    }
                                />
                            ))}
                        </Stack>
                    )}
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton
                    disabled={
                        productState.status !== 'success' ||
                        state.saving ||
                        unsavedChanges === false
                    }
                    onClick={handleSave}
                >
                    {state.saving ? <CircularProgress color="secondary" size={24} /> : 'Save'}
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function RequiredInfoDialog({
    open,
    onClose,
    eventId,
    loading,
    firstName,
    lastName,
    email,
    onUpdate,
    onSaved,
}) {
    const { notify } = useNotification();
    const [state, setState] = useState({
        saving: false,
        firstName,
        lastName,
        email,
    });

    useEffect(() => {
        if (open !== true) return;

        setState({
            saving: false,
            firstName,
            lastName,
            email,
        });
    }, [open, firstName, lastName, email]);

    const handleSave = () => {
        if (state.saving) return;

        setState((current) => ({ ...current, saving: true }));

        updateRequiredAttendeeInfo(eventId, state.firstName, state.lastName, state.email)
            .then(() => {
                notify.success('Attendee information updated');
                onUpdate(state.firstName, state.lastName, state.email);
                onSaved();
            })
            .catch(() => {
                notify.error('Unable to save attendee information');
                setState((current) => ({ ...current, saving: false }));
            });
    };

    const unsavedChanges =
        firstName !== state.firstName || lastName !== state.lastName || email !== state.email;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit Required Attendee Information</DialogTitle>
            <DialogContent dividers>
                {loading === true ? (
                    <LoadingRow>
                        <CircularProgress color="secondary" />
                    </LoadingRow>
                ) : (
                    <Stack spacing={1}>
                        <FormControlLabel
                            label="First Name"
                            control={
                                <Checkbox
                                    checked={state.firstName}
                                    onChange={() =>
                                        setState((current) => ({
                                            ...current,
                                            firstName: !current.firstName,
                                        }))
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label="Last Name"
                            control={
                                <Checkbox
                                    checked={state.lastName}
                                    onChange={() =>
                                        setState((current) => ({
                                            ...current,
                                            lastName: !current.lastName,
                                        }))
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label="Email Address"
                            control={
                                <Checkbox
                                    checked={state.email}
                                    onChange={() =>
                                        setState((current) => ({
                                            ...current,
                                            email: !current.email,
                                        }))
                                    }
                                />
                            }
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton
                    disabled={loading === true || state.saving || unsavedChanges === false}
                    onClick={handleSave}
                >
                    {state.saving ? <CircularProgress color="secondary" size={24} /> : 'Save'}
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function countRequiredFields(firstName, lastName, email) {
    return [firstName, lastName, email].filter(Boolean).length;
}

const Divider = styled(Box)(({ theme }) => ({
    borderTop: '1.5px solid',
    borderColor: theme.palette.grey[400],
}));

const SummaryCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
}));

const SummaryItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
}));

const WarningRow = styled(Box)(({ theme }) => ({
    color: theme.palette.warning.main,
    display: 'flex',
    alignItems: 'center',
}));

const WarningText = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.warning.main,
}));

const AdmissionIcon = styled(ConfirmationNumberIcon)(({ theme }) => ({
    height: 16,
    width: 16,
    marginRight: theme.spacing(1),
    flexShrink: 0,
}));

const AddonIcon = styled(AddShoppingCartIcon)(({ theme }) => ({
    height: 16,
    width: 16,
    marginRight: theme.spacing(1),
    flexShrink: 0,
}));

const CheckMark = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    color: theme.palette.success.main,
    flexShrink: 0,
}));

const CrossMark = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    color: theme.palette.error.main,
    flexShrink: 0,
}));
